import * as React from 'react';
import { clsx } from 'clsx';

export interface TableContainerProps {
  /**
   * Children to render inside the container (TableControls, Table, TableFooterPagination)
   */
  children: React.ReactNode;
  /**
   * Custom className
   */
  className?: string;
  /**
   * Custom style
   */
  style?: React.CSSProperties;
  /**
   * Test ID for testing
   */
  'data-testid'?: string;
}

const containerStyles: React.CSSProperties = {
  backgroundColor: '#ffffff',
  padding: '8px',
  borderRadius: '8px',
  width: '100%',
  boxSizing: 'border-box',
};

/**
 * TableContainer component - Arbor Design System
 *
 * A wrapper container for the full table experience.
 * Use to wrap TableControls, Table, and TableFooterPagination together
 * in a white box with 8px padding and 8px border radius.
 *
 * @example
 * ```tsx
 * <TableContainer>
 *   <TableControls {...controlsProps} />
 *   <Table {...tableProps} />
 *   <TableFooterPagination {...paginationProps} />
 * </TableContainer>
 * ```
 */
export const TableContainer = React.forwardRef<HTMLDivElement, TableContainerProps>(
  ({ children, className, style, 'data-testid': dataTestId }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('arbor-table-container', className)}
        style={{ ...containerStyles, ...style }}
        data-testid={dataTestId}
      >
        {children}
      </div>
    );
  }
);

TableContainer.displayName = 'TableContainer';
